import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Banner, Button, Card, FieldSelect, FieldText, LoadingAnimation, PageHeader } from "@knowtex-ai/components";
import { Plus, Search, WarningTriangle } from "iconoir-react";
import FoiaPageShell from "./FoiaPageShell";
import { fetchFoiaRequests } from "./api";
import { useToast } from "../../hooks/use-toast";
import { STATUS_CHIP_BY_STEP } from "./foiaSteps";
import {
  formatDate,
  formatRelativeTime,
  getAssignee,
  getCurrentStepPlainEnglish,
  getDueDateUrgency,
  getStatusChip,
} from "./foiaUtils";
import { T, S } from "../Karoline/theme";

const PER_PAGE = 50;

const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  ...[...new Set(Object.values(STATUS_CHIP_BY_STEP).map((s) => s.label))].map((label) => ({ value: label, label })),
];

const ASSIGNEE_OPTIONS = [
  { value: "", label: "Any assignee" },
  { value: "Cortney", label: "Cortney" },
  { value: "Engineering", label: "Engineering" },
];

const DUE_OPTIONS = [
  { value: "", label: "Any due date" },
  { value: "soon", label: "Due within 7 days" },
  { value: "overdue", label: "Due within 2 days or overdue" },
];

const URGENCY_COPY = {
  overdue: { label: "Due very soon", className: "foia-urgency foia-urgency--overdue" },
  soon: { label: "Due soon", className: "foia-urgency foia-urgency--soon" },
};

const COLUMNS = [
  { key: "request", label: "Tracking number", width: "16%" },
  { key: "status", label: "Status", width: "23%" },
  { key: "waitingOn", label: "Waiting on", width: "27%" },
  { key: "dueDate", label: "Due date", width: "18%", sortable: true },
  { key: "lastActivity", label: "Last activity", width: "16%" },
];

export default function FoiaRequestList({ setAuth, setUserOrg }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [dueFilter, setDueFilter] = useState("");
  const [dueDir, setDueDir] = useState("asc");
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchFoiaRequests()
      .then((data) => {
        if (!cancelled) setRequests(data?.requests || []);
      })
      .catch((err) => {
        if (!cancelled) {
          toast({ title: "Couldn't load FOIA requests", description: err.message, variant: "destructive" });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
  }, [query, statusFilter, assigneeFilter, dueFilter, dueDir]);

  const filtered = useMemo(() => {
    const direction = dueDir === "asc" ? 1 : -1;
    return requests
      .filter((request) => {
        if (query.trim() && !request.trackingNumber.toLowerCase().includes(query.trim().toLowerCase())) {
          return false;
        }
        if (statusFilter && getStatusChip(request).label !== statusFilter) return false;
        if (assigneeFilter && getAssignee(request) !== assigneeFilter) return false;
        if (dueFilter && getDueDateUrgency(request) !== dueFilter) return false;
        return true;
      })
      .sort((a, b) => direction * (new Date(a.dueDate) - new Date(b.dueDate)));
  }, [requests, query, statusFilter, assigneeFilter, dueFilter, dueDir]);

  const hasActiveFilters = Boolean(query || statusFilter || assigneeFilter || dueFilter);

  const clearFilters = () => {
    setQuery("");
    setStatusFilter("");
    setAssigneeFilter("");
    setDueFilter("");
  };

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const openRequest = (id) => navigate(`/foia/requests/${id}`);

  return (
    <FoiaPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
      <PageHeader
        title="FOIA Requests"
        subtitle="Track every FOIA data request from intake through delivery to the COR."
        actions={
          <Button icon={Plus} onClick={() => navigate("/foia/new")}>
            New FOIA request
          </Button>
        }
      />

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FieldText
          placeholder="Search by tracking number"
          icon={Search}
          value={query}
          onValueChange={setQuery}
          aria-label="Search by tracking number"
        />
        <FieldSelect
          items={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value || "")}
          placeholder="Any status"
          aria-label="Filter by status"
          triggerMinWidth="100%"
        />
        <FieldSelect
          items={ASSIGNEE_OPTIONS}
          value={assigneeFilter}
          onChange={(value) => setAssigneeFilter(value || "")}
          placeholder="Any assignee"
          aria-label="Filter by assignee"
          triggerMinWidth="100%"
        />
        <FieldSelect
          items={DUE_OPTIONS}
          value={dueFilter}
          onChange={(value) => setDueFilter(value || "")}
          placeholder="Any due date"
          aria-label="Filter by due date"
          triggerMinWidth="100%"
        />
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-gray-400">
            <LoadingAnimation size={28} />
            <span className="text-sm">Loading FOIA requests…</span>
          </div>
        ) : filtered.length === 0 ? (
          <Banner variant="info" heading="No requests match your filters">
            Try a different tracking number or clearing the filters below.
            {hasActiveFilters && (
              <div className="mt-3">
                <Button variant="secondary" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              </div>
            )}
          </Banner>
        ) : (
          <Card variant="elevated" size="small">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: 14,
                borderBottom: `1px solid ${T.border}`,
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 600 }}>FOIA Requests</span>
              <span style={{ fontSize: 12, color: T.textMuted }}>{total} total</span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {COLUMNS.map((col) => {
                      if (col.sortable) {
                        return (
                          <th
                            key={col.key}
                            onClick={() => setDueDir((dir) => (dir === "asc" ? "desc" : "asc"))}
                            aria-sort={dueDir === "asc" ? "ascending" : "descending"}
                            style={{ ...S.th, width: col.width, color: T.accent, cursor: "pointer", userSelect: "none" }}
                          >
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <span>{col.label}</span>
                              <span style={{ fontSize: 9, flexShrink: 0 }}>{dueDir === "asc" ? "▲" : "▼"}</span>
                            </span>
                          </th>
                        );
                      }
                      return (
                        <th key={col.key} style={{ ...S.th, width: col.width }}>
                          {col.label}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((request) => {
                    const status = getStatusChip(request);
                    const urgency = getDueDateUrgency(request);
                    const urgencyCopy = urgency && URGENCY_COPY[urgency];
                    const stepText = getCurrentStepPlainEnglish(request);
                    return (
                      <tr
                        key={request.id}
                        tabIndex={0}
                        onClick={() => openRequest(request.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") openRequest(request.id);
                        }}
                        aria-label={`Open FOIA request ${request.trackingNumber}`}
                        style={{ transition: "background 0.1s", cursor: "pointer" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = T.surface2)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                      >
                        <td style={S.td}>
                          <div style={{ ...S.mono, fontWeight: 600, overflowWrap: "anywhere" }}>{request.trackingNumber}</div>
                        </td>
                        <td style={S.td}>
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </td>
                        <td style={S.td}>{stepText}</td>
                        <td style={S.td}>
                          <div>{formatDate(request.dueDate)}</div>
                          {urgencyCopy && (
                            <div style={{ marginTop: 4 }}>
                              <span className={urgencyCopy.className}>
                                <WarningTriangle width={13} height={13} />
                                {urgencyCopy.label}
                              </span>
                            </div>
                          )}
                        </td>
                        <td style={{ ...S.td, ...S.muted }}>{formatRelativeTime(request.lastActivityAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 20px",
                borderTop: `1px solid ${T.border}`,
              }}
            >
              <span style={{ fontSize: 12, color: T.textMuted }}>
                Showing {(currentPage - 1) * PER_PAGE + 1}–{Math.min(currentPage * PER_PAGE, total)} of {total}
              </span>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <Button variant="secondary" size="small" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
                  Prev
                </Button>
                <span style={{ fontSize: 12, color: T.textMuted, padding: "0 8px" }}>
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="small"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </FoiaPageShell>
  );
}
